import axios from 'axios'
import { InteractionRequiredAuthError, type AccountInfo, type PublicClientApplication } from '@azure/msal-browser'
import { msalInstance, parentGraphTokenRequest } from '@config/auth/msalConfig'
import { usePortalModeStore } from '@stores/usePortalModeStore'
import { getPortalAccessToken } from './portalAccessToken'
import { toApiError } from './apiError'
import { getPortalsApiBaseUrl } from './portalApi'
import { instrumentPortalClient } from '@services/telemetry/browserTracing'

const GRAPH_API_BASE_URL = 'https://graph.microsoft.com/v1.0/'

async function getParentGraphToken(instance: PublicClientApplication, account?: AccountInfo) {
    const selectedAccount = account ?? instance.getActiveAccount() ?? instance.getAllAccounts()[0]
    if (!selectedAccount) throw new Error('No signed-in account')

    try {
        const response = await instance.acquireTokenSilent({
            ...parentGraphTokenRequest,
            account: selectedAccount,
        })
        return response.accessToken
    } catch (error) {
        if (!(error instanceof InteractionRequiredAuthError)) throw error
        const response = await instance.acquireTokenPopup({
            ...parentGraphTokenRequest,
            account: selectedAccount,
        })
        return response.accessToken
    }
}

function normalizeGraphPath(url?: string) {
    if (!url) return url
    if (url.startsWith(GRAPH_API_BASE_URL)) return url.slice(GRAPH_API_BASE_URL.length)
    return url.replace(/^\//, '')
}

const graphApi = axios.create()
instrumentPortalClient(graphApi)

graphApi.interceptors.request.use(async (config) => {
    const { hostMode, viewMode } = usePortalModeStore.getState()
    const isParent = hostMode === 'parent'

    config.baseURL = isParent
        ? GRAPH_API_BASE_URL
        : `${getPortalsApiBaseUrl()}/api/graph/`
    config.url = normalizeGraphPath(config.url)

    const token = isParent
        ? await getParentGraphToken(msalInstance)
        : await getPortalAccessToken(msalInstance)
    if (!token) throw new Error('Failed to acquire a portal access token')

    ;(config.headers as any).set?.('Authorization', `Bearer ${token}`)
    if (!isParent) {
        ;(config.headers as any).set?.('x-host-mode', hostMode)
        ;(config.headers as any).set?.('x-view-mode', viewMode)
    }

    return config
})

graphApi.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(toApiError(error)),
)

export { graphApi }
